/**
 * Task Service
 * TASK-355: Task & Follow-up Management System
 * TASK-358: Frontend AddTaskModal Component
 *
 * Provides API methods for task CRUD operations.
 */

import { httpClient } from '../lib/httpClient';
import {
  TaskCreateRequest,
  TaskUpdateRequest,
  TaskSnoozeRequest,
  TaskResponse,
  TaskDetailResponse,
  TaskListResponse,
  TaskDashboardStatsResponse,
  TaskListFilters,
  UnresolvableArtifactsResponse,
  AttachTaskArtifactRequest,
  TaskArtifactBinding,
  TaskBindingMutationRequest,
  TaskBindingResponse,
  TaskOutcome,
} from '../types/tasks';

/**
 * TASK-922 — everything `PATCH /complete` accepts beyond the id.
 *
 * `snoozedUntil` is only read when `outcome` is `deferred`; that is the one outcome that
 * changes the task's own schedule rather than leaving the subject to the evaluator.
 */
export interface CompleteTaskOptions {
  exceptionReason?: string;
  outcome?: TaskOutcome;
  outcomeRationale?: string;
  snoozedUntil?: string;
}

// User type for assignee selection
export interface UserListItem {
  id: string;
  email: string;
  display_name: string;
  first_name?: string;
  last_name?: string;
  role: string;
  is_active: boolean;
}

export interface UserListResponse {
  users: UserListItem[];
  total: number;
  page: number;
  page_size: number;
  total_pages: number;
}

class TaskService {
  /**
   * Create a new task
   */
  async createTask(task: TaskCreateRequest): Promise<TaskResponse> {
    try {
      const data = await httpClient.post<TaskResponse>('/api/tasks', task);
      return data;
    } catch (error) {
      console.error('Error creating task:', error);
      throw error;
    }
  }

  /**
   * Fetch tasks with optional filters
   */
  async fetchTasks(filters?: TaskListFilters): Promise<TaskListResponse> {
    try {
      const params: Record<string, any> = {};

      if (filters) {
        if (filters.status) params.status = filters.status;
        if (filters.task_type) params.task_type = filters.task_type;
        if (filters.client_id) params.client_id = filters.client_id;
        if (filters.assignee_user_id) params.assignee_user_id = filters.assignee_user_id;
        if (filters.priority) params.priority = filters.priority;
        if (filters.due_before) params.due_before = filters.due_before;
        if (filters.due_after) params.due_after = filters.due_after;
        if (filters.include_snoozed !== undefined) params.include_snoozed = filters.include_snoozed;
        if (filters.limit) params.limit = filters.limit;
        if (filters.offset) params.offset = filters.offset;
        if (filters.sort_by) params.sort_by = filters.sort_by;
        if (filters.sort_order) params.sort_order = filters.sort_order;
      }

      const data = await httpClient.get<TaskListResponse>('/api/tasks', { params });
      return data;
    } catch (error) {
      console.error('Error fetching tasks:', error);
      throw error;
    }
  }

  /**
   * Get dashboard statistics
   */
  async getDashboardStats(assigneeUserId?: string): Promise<TaskDashboardStatsResponse> {
    try {
      const params: Record<string, any> = {};
      if (assigneeUserId) params.assignee_user_id = assigneeUserId;

      const data = await httpClient.get<TaskDashboardStatsResponse>('/api/tasks/dashboard-stats', {
        params,
      });
      return data;
    } catch (error) {
      console.error('Error fetching dashboard stats:', error);
      throw error;
    }
  }

  /**
   * Get a single task by ID
   */
  async getTask(taskId: string): Promise<TaskResponse> {
    try {
      const data = await httpClient.get<TaskResponse>(`/api/tasks/${taskId}`);
      return data;
    } catch (error) {
      console.error('Error fetching task:', error);
      throw error;
    }
  }

  /**
   * TASK-702 — the same endpoint as `getTask`, typed for everything it has always returned.
   *
   * `GET /api/tasks/{id}` has returned `{task, events, artifacts}` since TASK-684 and the
   * client dropped all but `task`, so the RM could see THAT the agent did something and never
   * WHAT. `trail_complete` / `trail_gaps` are derived server-side on read.
   */
  async getTaskDetail(taskId: string): Promise<TaskDetailResponse> {
    const data = await httpClient.get<TaskDetailResponse>(`/api/tasks/${taskId}`);
    return {
      ...data,
      events: data.events ?? [],
      artifacts: data.artifacts ?? [],
      bindings: data.bindings ?? [],
      // An older server that does not derive these must not read as "trail complete" —
      // absent is not the same as verified, so default to no claim either way.
      trail_complete: data.trail_complete ?? true,
      trail_gaps: data.trail_gaps ?? [],
    };
  }

  /**
   * Update a task
   * Returns 409 Conflict if version mismatch (concurrent update)
   */
  async updateTask(taskId: string, updates: TaskUpdateRequest): Promise<TaskResponse> {
    try {
      const data = await httpClient.patch<TaskResponse>(`/api/tasks/${taskId}`, updates);
      return data;
    } catch (error: any) {
      if (error.response?.status === 409) {
        console.warn('Task was modified by another user');
        // Re-throw with special flag for UI to handle
        throw {
          ...error,
          isVersionConflict: true,
          currentVersion: error.response?.data?.detail?.current_version,
          yourVersion: error.response?.data?.detail?.your_version,
        };
      }
      console.error('Error updating task:', error);
      throw error;
    }
  }

  /**
   * TASK-758: every artifact on the account whose pointer does not resolve.
   *
   * Read-only. The repair TASK is created by the invariant monitor, never by looking.
   */
  async fetchUnresolvableArtifacts(): Promise<UnresolvableArtifactsResponse> {
    return httpClient.get<UnresolvableArtifactsResponse>('/api/tasks/artifacts/unresolvable');
  }

  /**
   * Mark a task as completed.
   *
   * TASK-758: `exceptionReason` is sent ONLY when the RM signs off work whose artifact could
   * not be opened. The server validates it and records it on the `signed_off` event.
   *
   * TASK-922: `outcome` is REQUIRED by the server for any task carrying `subject_key`, and
   * nothing here ever sent it — every completion of Radar-raised work answered 400
   * `outcome_required`, and not one completion in the database carries an outcome. The
   * options object replaced a positional `exceptionReason` because there are now four fields
   * and three of them travel together.
   */
  async completeTask(
    taskId: string,
    options?: CompleteTaskOptions,
  ): Promise<TaskResponse> {
    const body: Record<string, string> = {};
    if (options?.exceptionReason) body.exception_reason = options.exceptionReason;
    if (options?.outcome) body.outcome = options.outcome;
    if (options?.outcomeRationale) body.outcome_rationale = options.outcomeRationale;
    // Only meaningful with `deferred`; the server ignores it otherwise, and sending it
    // anyway would let a stale date from an abandoned pick ride along on a later choice.
    if (options?.outcome === 'deferred' && options.snoozedUntil) {
      body.snoozed_until = options.snoozedUntil;
    }

    try {
      const data = await httpClient.patch<TaskResponse>(
        `/api/tasks/${taskId}/complete`,
        Object.keys(body).length ? body : undefined,
      );
      return data;
    } catch (error) {
      console.error('Error completing task:', error);
      throw error;
    }
  }

  /**
   * TASK-743: attach an artifact to a task — upserts the binding, inserts evidence.
   * Reattaching a withdrawn identity reactivates the SAME binding server-side.
   */
  async attachTaskArtifact(
    taskId: string,
    body: AttachTaskArtifactRequest,
  ): Promise<{ success: boolean; binding?: TaskArtifactBinding }> {
    return httpClient.post(`/api/tasks/${taskId}/artifacts`, body);
  }

  /**
   * TASK-743: classify / withdraw / reactivate a binding. `withdraw` requires `reason`.
   */
  async mutateTaskBinding(
    taskId: string,
    body: TaskBindingMutationRequest,
  ): Promise<TaskBindingResponse> {
    return httpClient.patch(`/api/tasks/${taskId}/bindings`, body);
  }

  /**
   * Snooze a task until a specific date/time
   */
  async snoozeTask(taskId: string, snoozeUntil: string): Promise<TaskResponse> {
    try {
      const data = await httpClient.patch<TaskResponse>(`/api/tasks/${taskId}/snooze`, {
        snooze_until: snoozeUntil,
      } as TaskSnoozeRequest);
      return data;
    } catch (error) {
      console.error('Error snoozing task:', error);
      throw error;
    }
  }

  /**
   * Delete (soft delete) a task
   */
  async deleteTask(taskId: string): Promise<{ success: boolean; message: string }> {
    try {
      const data = await httpClient.delete<{ success: boolean; message: string }>(
        `/api/tasks/${taskId}`
      );
      return data;
    } catch (error) {
      console.error('Error deleting task:', error);
      throw error;
    }
  }

  // ============= Convenience Methods =============

  /**
   * Fetch overdue tasks
   */
  async fetchOverdueTasks(limit = 10): Promise<TaskListResponse> {
    return this.fetchTasks({
      status: 'overdue',
      limit,
      sort_by: 'due_date',
      sort_order: 'asc',
    });
  }

  /**
   * Fetch tasks due today
   */
  async fetchTasksDueToday(): Promise<TaskListResponse> {
    const today = new Date();
    const startOfDay = new Date(today.setHours(0, 0, 0, 0)).toISOString();
    const endOfDay = new Date(today.setHours(23, 59, 59, 999)).toISOString();

    return this.fetchTasks({
      status: 'pending',
      due_after: startOfDay,
      due_before: endOfDay,
      sort_by: 'priority',
      sort_order: 'desc',
    });
  }

  /**
   * Fetch tasks for a specific client
   */
  async fetchClientTasks(clientId: string, limit = 50): Promise<TaskListResponse> {
    return this.fetchTasks({
      client_id: clientId,
      limit,
      sort_by: 'due_date',
      sort_order: 'asc',
    });
  }

  /**
   * Fetch high priority pending tasks
   */
  async fetchHighPriorityTasks(limit = 10): Promise<TaskListResponse> {
    return this.fetchTasks({
      status: 'pending',
      priority: 'high',
      limit,
      sort_by: 'due_date',
      sort_order: 'asc',
    });
  }

  /**
   * Fetch users from the account for task assignment
   * TASK-370: Assignment notifications
   */
  async fetchAccountUsers(): Promise<UserListItem[]> {
    try {
      const data = await httpClient.get<UserListResponse>('/api/admin/users', {
        params: { is_active: true, page_size: 100 },
      });
      return data.users || [];
    } catch (error) {
      console.error('Error fetching users:', error);
      return [];
    }
  }

  /**
   * TASK-925: the client's RM of record — the `primary` row in `client_assignments`.
   *
   * For DISPLAY only. The server owns the ownership rule (`core/task_ownership.py`) and
   * applies it on write; this exists so the advisor can see who a task will land on before
   * they create it, not so the client can decide it. Sending a resolved assignee from here
   * would put the same rule in two places and let them drift.
   *
   * Already proxied: the gateway forwards `/api/admin/*` to auth-service, where this router
   * is mounted — no new route.
   */
  async fetchClientRmOfRecord(clientId: string): Promise<RmOfRecord> {
    try {
      const data = await httpClient.get<{
        assignments?: Array<{
          user_id: string; assignment_type: string;
          display_name?: string | null; email?: string | null;
        }>;
      }>(`/api/admin/clients/${clientId}/assignments`);
      const primary = (data.assignments || []).find((a) => a.assignment_type === 'primary');
      // `none` is a FACT about the client — the server will fall back to the creator, and the
      // modal may say so.
      return primary
        ? {
            state: 'resolved',
            userId: primary.user_id,
            name: primary.display_name || primary.email || undefined,
          }
        : { state: 'none' };
    } catch (error) {
      // `unknown` is NOT the same fact. A 403 (no `clients:read`), a timeout or a 500 tells us
      // nothing about who owns this client — and answering "you" here would print a promise
      // the server then breaks by assigning the RM. Say nothing rather than say wrong.
      return { state: 'unknown' };
    }
  }
}

/**
 * TASK-925: three distinct answers, because two of them are not interchangeable — see
 * `fetchClientRmOfRecord`.
 */
export type RmOfRecord =
  // `name` rides along because the endpoint already returns it. Resolving it through the
  // account roster instead would need `/api/admin/users`, which is admin-only — so an advisor
  // would be shown a generic phrase for a person the response had already named.
  | { state: 'resolved'; userId: string; name?: string }
  | { state: 'none' }
  | { state: 'unknown' };

export const taskService = new TaskService();
export default taskService;
